/**
 * RestoID — Register: set the account password.
 * ---------------------------------------------------------------------------
 * RestoID presentation on the PRISTINE upstream `src/pages/RegisterPassword`
 * logic. Every hook, guard, error handler and navigation call is carried over
 * verbatim — the fast password-policy check, `continueRegisterWithPassword`, the
 * register-interaction error handlers (profile fulfillment / MFA / email blocked /
 * missing passkey), the `user.username_already_in_use` alert-then-go-back branch,
 * the `password.rejected` handler and the final `redirectTo`. Only the markup
 * changes. Swapped in via `src/App.tsx`.
 *
 * Reuses `SetPasswordFields`, the RestoID stand-in for `@/containers/SetPassword`
 * that `ResetPassword.tsx` already uses: same react-hook-form logic, same i18n
 * keys, RestoID `Field` controls. It is NOT given `isForgotPassword` here, exactly
 * as upstream doesn't, so its `HiddenIdentifierInput` pairs the new password with
 * the REGISTRATION identifier rather than the forgot-password one.
 *
 * Deliberate differences from upstream, and why:
 *   - `SecondaryPageLayout` → `FirstScreenLayout` + `ScreenHead`. Upstream renders a
 *     NavBar title AND a page title; RestoID shows one. The NavBar's back button was
 *     the only thing lost with it, so `ScreenHead` carries the same `navigate(-1)`
 *     (search-params-preserving) back action as a RestoID pill. Upstream passed no
 *     `onSkip`/`notification`, so nothing else from that layout is dropped.
 *   - Title key `description.new_password` → `description.set_password`. Upstream
 *     reuses the reset-password title here, which reads wrong on registration
 *     ("Новый пароль" / "New password" when there is no old one); the RestoID design
 *     spec (`custom/design-reference/app.js`, `RegisterPasswordScreen`) specifies
 *     `description.set_password` ("Задать пароль" / "Set password"). Still a real
 *     Logto phrase key, so Custom Phrases keeps working.
 *   - The subtitle stays upstream's `requirementsDescription` (the localized password
 *     policy hint). The design spec puts `description.enter_username_description`
 *     there, which is a copy-paste slip in the spec — it describes USERNAME rules on a
 *     password screen — so the upstream copy wins.
 *   - The policy's `length.min` and `characterTypes.min` are no longer destructured.
 *     Upstream read them only to feed `descriptionProps`, and `SecondaryPageLayout`
 *     ignores `descriptionProps` whenever `description` is an element rather than a
 *     key — which it always was here. They interpolated nothing; `length.max` (the
 *     input `maxLength`) is still read and still passed.
 */
import { InteractionEvent, SignInIdentifier } from '@logto/schemas';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import { continueRegisterWithPassword } from '@/apis/experience';
import useApi from '@/hooks/use-api';
import { usePromiseConfirmModal } from '@/hooks/use-confirm-modal';
import useErrorHandler, { type ErrorHandlers } from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import usePasswordPolicyChecker from '@/hooks/use-password-policy-checker';
import usePasswordRejectionErrorHandler from '@/hooks/use-password-rejection-handler';
import { usePasswordPolicy, useSieMethods } from '@/hooks/use-sie';
import useSubmitInteractionErrorHandler from '@/hooks/use-submit-interaction-error-handler';

import SetPasswordFields from '../components/SetPasswordFields';
import { ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

const RegisterPassword = () => {
  const { signUpMethods } = useSieMethods();

  const { t } = useTranslation();
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const redirectTo = useGlobalRedirectTo();
  const { show } = usePromiseConfirmModal();
  const [errorMessage, setErrorMessage] = useState<string>();
  const clearErrorMessage = useCallback(() => {
    setErrorMessage(undefined);
  }, []);

  const checkPassword = usePasswordPolicyChecker({ setErrorMessage });
  const asyncRegisterPassword = useApi(continueRegisterWithPassword);
  const handleError = useErrorHandler();

  const preRegisterErrorHandler = useSubmitInteractionErrorHandler(InteractionEvent.Register, {
    replace: true,
  });
  const passwordRejectionErrorHandler = usePasswordRejectionErrorHandler({ setErrorMessage });

  const errorHandlers: ErrorHandlers = useMemo(
    () => ({
      // In case the username submitted on the previous page has already been taken.
      'user.username_already_in_use': async (error) => {
        await show({ type: 'alert', ModalContent: error.message, cancelText: 'action.got_it' });
        navigate(-1);
      },
      ...preRegisterErrorHandler,
      ...passwordRejectionErrorHandler,
    }),
    [preRegisterErrorHandler, passwordRejectionErrorHandler, show, navigate]
  );

  const onSubmitHandler = useCallback(
    async (password: string) => {
      const success = await checkPassword(password);

      if (!success) {
        return;
      }

      const [error, result] = await asyncRegisterPassword(password);

      if (error) {
        await handleError(error, errorHandlers);
        return;
      }

      if (result) {
        await redirectTo(result.redirectTo);
      }
    },
    [asyncRegisterPassword, checkPassword, errorHandlers, handleError, redirectTo]
  );

  const {
    policy: {
      length: { max },
    },
    requirementsDescription,
  } = usePasswordPolicy();

  if (!signUpMethods.includes(SignInIdentifier.Username)) {
    return <ErrorPage />;
  }

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'description.set_password' }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('description.set_password')}
          sub={requirementsDescription}
        />
        <SetPasswordFields
          autoFocus
          errorMessage={errorMessage}
          maxLength={max}
          clearErrorMessage={clearErrorMessage}
          onSubmit={onSubmitHandler}
        />
      </Stack>
    </FirstScreenLayout>
  );
};

export default RegisterPassword;
